import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';

import { HUMAN_APPROVAL_WORDING, type HumanApprovalToolType } from './human-approval-utils';

export { APPROVAL_TO_VALUE, toApprovalOption } from './human-approval-utils';
export type { HumanApprovalOption, HumanApprovalToolType } from './human-approval-utils';

type HumanApprovalFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  id?: string;
  disabled?: boolean;
  toolType?: HumanApprovalToolType;
};

export function HumanApprovalField<TFieldValues extends FieldValues>({
  control,
  name,
  id = 'humanApproval',
  disabled = false,
  toolType = 'api',
}: HumanApprovalFieldProps<TFieldValues>) {
  const { t } = useTranslation();
  const wording = HUMAN_APPROVAL_WORDING[toolType];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <label htmlFor={id} className="text-[13px] font-medium leading-6">
          {t('HUMAN_APPROVAL', 'Human approval')}
        </label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger type="button" className="cursor-help">
              <Info className="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-[13px] leading-6">
                {t(wording.helpKey, { defaultValue: wording.helpFallback })}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
            <SelectTrigger id={id}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">
                {t(wording.autoKey, { defaultValue: wording.autoFallback })}
              </SelectItem>
              <SelectItem value="always">
                {t('HUMAN_APPROVAL_ALWAYS', 'Always ask')}
              </SelectItem>
              <SelectItem value="never">
                {t('HUMAN_APPROVAL_NEVER', 'Never ask')}
              </SelectItem>
            </SelectContent>
          </Select>
        )}
      />
    </div>
  );
}
